import { useEffect, useMemo, useSyncExternalStore } from 'react';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowControlPerformMode } from '@/types/player/simulation-cue-sheet.types';
import { useQueryClient } from '@tanstack/react-query';
import { useGetSimulationCueSheetQuery } from '@/state/queries/player/use-get-simulation-cue-sheet';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { applySimulationCuePlayability } from '@/helpers/simulation-cue-playability.helper';
import { useEventStore } from '@/state/stores/use-event-store';
import { useFlowStore } from '@/state/stores/use-flow-store';
import {
  resolveFlowControlSimulationFlow,
  sessionPlaylistFingerprintForFlow,
  simulationFlowPlayabilityKey,
} from '@/helpers/simulation-flow.helper';

/** Re-read session playlists when GET_SESSION query cache updates (without setState during render). */
function useSessionPlaylistFingerprint(flowProp?: FlowDto): string {
  const queryClient = useQueryClient();

  return useSyncExternalStore(
    (onStoreChange) =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event?.query?.queryKey[0] === GET_SESSION_QUERY_KEY) {
          onStoreChange();
        }
      }),
    () => sessionPlaylistFingerprintForFlow(resolveFlowControlSimulationFlow(flowProp)),
    () => '',
  );
}

/** Loads the simulation cue sheet into the shared store (slot list + transport + preview). */
export function useHydrateFlowControlSimulationCueSheet(
  flowProp: FlowDto | undefined,
  planFlowId: string | undefined,
  performMode: FlowControlPerformMode,
) {
  const hydrateCueSheet = useFlowControlSimulationCueStore((s) => s.hydrateCueSheet);
  const { data: cueSheet } = useGetSimulationCueSheetQuery(planFlowId, {
    enabled: !!planFlowId && performMode !== 'play',
  });

  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flow = useFlowStore((s) => s.flow);
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const sessionPlaylistFingerprint = useSessionPlaylistFingerprint(flowProp);
  const simulationFlow = useMemo(
    () => resolveFlowControlSimulationFlow(flowProp),
    [flowProp, flowPlan, flow, eventSessionsPlan, sessionPlaylistFingerprint],
  );
  const playabilityKey = `${simulationFlowPlayabilityKey(simulationFlow)}|${sessionPlaylistFingerprint}`;

  useEffect(() => {
    if (performMode === 'play' || !cueSheet || !planFlowId) {
      return;
    }
    const rows = simulationFlow ? applySimulationCuePlayability(cueSheet.rows, simulationFlow) : cueSheet.rows;
    hydrateCueSheet(planFlowId, rows, cueSheet.revision, simulationFlow);
  }, [cueSheet, hydrateCueSheet, performMode, planFlowId, simulationFlow]);

  // Flow plan updates (websocket sync) refresh row playability without treating it as a new cue sheet.
  useEffect(() => {
    if (!simulationFlow || !planFlowId || performMode !== 'simulation') {
      return;
    }
    const state = useFlowControlSimulationCueStore.getState();
    if (state.planFlowId !== planFlowId || !state.revision || state.rows.length === 0) {
      return;
    }
    hydrateCueSheet(
      planFlowId,
      applySimulationCuePlayability(state.rows, simulationFlow),
      state.revision,
      simulationFlow,
    );
  }, [hydrateCueSheet, performMode, planFlowId, playabilityKey, simulationFlow]);
}
